import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter, Route, Routes, Navigate, useParams } from 'react-router-dom';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { ApolloProvider } from '@apollo/client';
import { apolloClient } from './config/apollo';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import PublicLayout from './components/PublicLayout';
import PersonalLayout from './components/PersonalLayout';
import OrgAdminLayout from './components/OrgAdminLayout';
import PreviewLayout from './components/PreviewLayout';
import RequireAuth from './components/RequireAuth';
import RequireOrg from './components/RequireOrg';

import HomePage from './pages/HomePage';
import DebugPage from './pages/DebugPage';
import AuthPage from './pages/AuthPage';
import PlansPage from './pages/PlansPage';
import PlanDetailPage from './pages/PlanDetailPage';
import ParticipantWorkspacePage from './pages/ParticipantWorkspacePage';
import TodayPage from './pages/TodayPage';
import JoinPage from './pages/JoinPage';
import LoginPage from './pages/LoginPage';
import IntakePage from './pages/IntakePage';
import RequestThreadPage from './pages/RequestThreadPage';
import GuideLandingPage from './pages/GuideLandingPage';
import JourneyJoinPage from './pages/JourneyJoinPage';
import GuidesPage from './pages/GuidesPage';
import CreateGuidePage from './pages/CreateGuidePage';
import GuideImportFromUrlPage from './pages/GuideImportFromUrlPage';
import EditGuidePage from './pages/EditGuidePage';
import JobsPage from './pages/GenerationJobsPage';
import JobDetailPage from './pages/GenerationJobDetailPage';
import OrgPickerPage from './pages/OrgPickerPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import { ToastProvider } from './contexts/ToastContext';
import CreateOrgPage from './pages/CreateOrgPage';
import SettingsPage from './pages/SettingsPage';
import OrgIntegrationsPage from './pages/OrgIntegrationsPage';
import PlatformAdminIntegrationsPage from './pages/PlatformAdminIntegrationsPage';
import PersonalConnectionsPage from './pages/PersonalConnectionsPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import DocumentGroupsPage from './pages/DocumentGroupsPage';
import DocumentGroupDetailPage from './pages/DocumentGroupDetailPage';
import SourceLibraryPage from './pages/SourceLibraryPage';
import WebCrawlsPage from './pages/WebCrawlsPage';
import WebCrawlDetailPage from './pages/WebCrawlDetailPage';
import OrganizationBrandingPage from './pages/OrganizationBrandingPage';
import EvalPage from './pages/EvalPage';
import PreviewGuidePage from './pages/PreviewGuidePage';
import PreviewResultPage from './pages/PreviewResultPage';
import PreviewApplyPage from './pages/PreviewApplyPage';
import PreviewLibraryPage from './pages/PreviewLibraryPage';
import PilotLandingPage from './pages/PilotLandingPage';
import PilotDashboardPage from './pages/PilotDashboardPage';
import MethodologyPage from './pages/MethodologyPage';
import PilotAdminPage from './pages/PilotAdminPage';

const theme = createTheme({
  palette: {
    mode: 'light',
  },
  // PLN-201/PLN-202 — override MUI's Roboto default so any unstyled MUI
  // surface (Snackbar, Tooltip, ListItemText, modal default text…)
  // resolves to Public Sans and the page reads as a single typographic
  // voice instead of MUI-default mixed with pilot cream brand fonts.
  typography: {
    fontFamily:
      '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

/** Demo subdomains always land on the preview guide page */
const DEMO_HOSTNAMES = ['demo.planapus.com', 'demo.apply.pub'];

function isDemoHost(): boolean {
  return DEMO_HOSTNAMES.includes(window.location.hostname);
}

/**
 * Pilot hostnames land on the pilot page. The app uses HashRouter, so the
 * only real route is the part after `#` — a bare `/` or a server path like
 * `/pilot` boots the app with no hash, hits RootRedirect, and (without this
 * list) falls through to /explore. Listing a hostname here makes its bare
 * root — and any non-hash path — redirect to `/#/pilot` instead. The query
 * string is carried through (invite links arrive as /?invite=<code>).
 *
 * planapus.com / planapus-d83dd.web.app are included intentionally: the
 * primary domain should surface the pilot, not /explore.
 */
const PILOT_HOSTNAMES = [
  'pilot.planapus.com',
  'pilot-planapus.web.app',
  'planapus.com',
  'planapus-d83dd.web.app',
];

function isPilotHost(): boolean {
  return PILOT_HOSTNAMES.includes(window.location.hostname);
}

/** Redirect root: pilot → /pilot, demo → /preview/guide, authenticated → /app, otherwise → /pilot */
function RootRedirect() {
  const { user, loading } = useAuth();
  if (isPilotHost())
    return (
      <Navigate
        to={{ pathname: '/pilot', search: window.location.search }}
        replace
      />
    );
  if (isDemoHost()) return <Navigate to="/preview/guide" replace />;
  if (loading) return null;
  // /explore is blocked — logged-out visitors land on the pilot, not the
  // public-guides page.
  return <Navigate to={user ? '/app' : '/pilot'} replace />;
}

/** Rewrite /plans/* → /app/plans/* */
function LegacyPlanRedirect() {
  const { '*': rest } = useParams();
  return <Navigate to={`/app/plans/${rest ?? ''}`} replace />;
}

/** Rewrite /guides/* → /org/guides/* */
function LegacyGuideRedirect() {
  const { '*': rest } = useParams();
  return <Navigate to={`/org/guides/${rest ?? ''}`} replace />;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <ApolloProvider client={apolloClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HashRouter>
          <ToastProvider>
            <AuthProvider>
              <OrgProvider>
              <Routes>
                {/* Standalone pages (no layout) */}
                <Route path="/join" element={<JoinPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/intake/:slug" element={<IntakePage />} />
                <Route path="/request/:slug" element={<RequestThreadPage />} />
                <Route path="/o/:orgSlug/g/:guideSlug" element={<GuideLandingPage />} />
                <Route path="/g/:orgSlug/:guideSlug" element={<GuideLandingPage />} />
                <Route path="/p/:orgSlug/:guideSlug" element={<GuideLandingPage />} />
                <Route path="/j/join" element={<JourneyJoinPage />} />
                <Route path="/pilot" element={<PilotLandingPage />} />
                <Route path="/pilot/methodology" element={<MethodologyPage />} />
                <Route path="/pilot/admin" element={<PilotAdminPage />} />

                {/* Preview teaser shell (no auth required) */}
                <Route path="/preview" element={<PreviewLayout />}>
                  <Route index element={<Navigate to="/preview/guide" replace />} />
                  <Route path="apply" element={<PreviewApplyPage />} />
                  <Route path="guide" element={<PreviewGuidePage />} />
                  <Route path="guide/:previewPlanId" element={<PreviewResultPage />} />
                  <Route path="library" element={<PreviewLibraryPage />} />
                </Route>

                {/* Public shell */}
                <Route element={<PublicLayout />}>
                  {/* /explore is blocked — bounce anyone who lands on it to the pilot. */}
                  <Route path="/explore" element={<Navigate to="/pilot" replace />} />
                  <Route path="/auth" element={<AuthPage />} />
                </Route>

                {/* Personal shell (auth required) */}
                <Route element={<RequireAuth />}>
                  <Route path="/app" element={<PersonalLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="plans" element={<PlansPage />} />
                    <Route path="plans/:id" element={<PlanDetailPage />} />
                    <Route path="plans/:id/:tab" element={<PlanDetailPage />} />
                    <Route path="plans/:id/tasks/:stepId" element={<PlanDetailPage />} />
                    <Route path="plans/:id/participant" element={<ParticipantWorkspacePage />} />
                    <Route path="today" element={<TodayPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="settings/connections" element={<PersonalConnectionsPage />} />
                    <Route path="settings/notifications" element={<NotificationSettingsPage />} />
                    <Route path="debug" element={<DebugPage />} />
                  </Route>
                </Route>

                {/* Org admin shell (auth + org required for guides) */}
                <Route element={<RequireAuth />}>
                  <Route path="/org" element={<OrgAdminLayout />}>
                    <Route index element={<OrgPickerPage />} />
                    <Route path="new" element={<CreateOrgPage />} />
                    <Route path="guides" element={<RequireOrg />}>
                      <Route index element={<GuidesPage />} />
                      <Route path="new" element={<CreateGuidePage />} />
                      <Route path="import-url" element={<GuideImportFromUrlPage />} />
                      <Route path=":guideId/edit" element={<EditGuidePage />} />
                    </Route>
                    <Route path="groups" element={<RequireOrg />}>
                      <Route index element={<DocumentGroupsPage />} />
                      <Route path=":groupId" element={<DocumentGroupDetailPage />} />
                    </Route>
                    <Route path="sources" element={<RequireOrg />}>
                      <Route index element={<SourceLibraryPage />} />
                    </Route>
                    <Route path="crawls" element={<RequireOrg />}>
                      <Route index element={<WebCrawlsPage />} />
                      <Route path=":crawlId" element={<WebCrawlDetailPage />} />
                    </Route>
                    <Route path="branding" element={<RequireOrg />}>
                      <Route index element={<OrganizationBrandingPage />} />
                    </Route>
                    <Route path="eval" element={<EvalPage />} />
                    <Route path="eval/:tab" element={<EvalPage />} />
                    <Route path="eval/:tab/:reportId" element={<EvalPage />} />
                    <Route path="jobs" element={<JobsPage />} />
                    <Route path="jobs/:jobId" element={<JobDetailPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="integrations" element={<RequireOrg />}>
                      <Route index element={<OrgIntegrationsPage />} />
                    </Route>
                    {/*
                     * S12 — Platform Admin dashboard. Intentionally
                     * unlinked from the main nav; URL-shared with
                     * platform ops. Server enforces the gate via
                     * `requirePlatformAdmin`; the page itself surfaces
                     * FORBIDDEN with a friendly alert rather than
                     * redirecting (so admins debugging claim provisioning
                     * can see why they were rejected).
                     */}
                    <Route
                      path="platform-admin/integrations"
                      element={<PlatformAdminIntegrationsPage />}
                    />
                    <Route
                      path="platform-admin/pilot-dashboard"
                      element={<PilotDashboardPage />}
                    />
                  </Route>
                </Route>

                {/* Root redirect */}
                <Route path="/" element={<RootRedirect />} />

                {/* Legacy redirects */}
                <Route path="/plans" element={<Navigate to="/app/plans" replace />} />
                <Route path="/plans/*" element={<LegacyPlanRedirect />} />
                <Route path="/guides" element={<Navigate to="/org/guides" replace />} />
                <Route path="/guides/*" element={<LegacyGuideRedirect />} />
                <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
                <Route path="/today" element={<Navigate to="/app/today" replace />} />
                <Route path="/orgs/new" element={<Navigate to="/org/new" replace />} />
                <Route path="/orgs/settings" element={<Navigate to="/app/settings" replace />} />
                <Route path="/debug" element={<Navigate to="/app/debug" replace />} />

                {/*
                 * PLN-002 — catch-all. Previously any unmatched hash
                 * (`#/story`, `#/sandbox`, `#/demo`, `#/vision`, typos,
                 * stale bookmarks) rendered a blank `#root` with a
                 * "No routes matched location" warning. The pilot
                 * deliberately keeps view-mode in localStorage rather
                 * than the URL — those were never real routes.
                 * Bounce anything unmatched to the pilot (the app's
                 * public home) instead of a dead page.
                 */}
                <Route path="*" element={<Navigate to="/pilot" replace />} />
              </Routes>
              </OrgProvider>
            </AuthProvider>
          </ToastProvider>
        </HashRouter>
      </ThemeProvider>
    </ApolloProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
